import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { ArrowLeft, Info, User, Users, MapPin, Loader2, Save } from 'lucide-react';
import { updateProfileSchema, type ApiError, type UpdateProfileInput } from '@pms/shared';
import { profileService } from '@/services/profile.service';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

function SectionHeader({ icon, title, hint }: { icon: React.ReactNode; title: string; hint?: string }) {
  return (
    <CardHeader className="border-b border-border/40 px-4 py-2.5">
      <CardTitle className="flex items-center gap-2 text-sm font-semibold text-primary">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">{icon}</div>
        {title}
        {hint && <span className="ml-auto text-xs font-normal text-muted-foreground">{hint}</span>}
      </CardTitle>
    </CardHeader>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1 text-xs text-destructive">{message}</p>;
}

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const MARITAL = ['single', 'married', 'divorced', 'widowed'];

export function AccountManagePage() {
  const navigate = useNavigate();
  const { user, profile, refreshMe } = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<UpdateProfileInput>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (user) {
      reset({
        first_name:           user.first_name ?? undefined,
        last_name:            user.last_name ?? undefined,
        phone:                user.phone ?? undefined,
        dob:                  profile?.dob ?? undefined,
        gender:               (profile?.gender as 'male' | 'female' | 'other' | null) ?? undefined,
        blood_group:          profile?.blood_group ?? undefined,
        marital_status:       profile?.marital_status ?? undefined,
        emergency_contact:    profile?.emergency_contact ?? undefined,
        father_name:          profile?.father_name ?? undefined,
        mother_name:          profile?.mother_name ?? undefined,
        spouse_name:          profile?.spouse_name ?? undefined,
        address_line1:        profile?.address_line1 ?? undefined,
        address_line2:        profile?.address_line2 ?? undefined,
        city:                 profile?.city ?? undefined,
        state:                profile?.state ?? undefined,
        postal_code:          profile?.postal_code ?? undefined,
        physically_challenged: profile?.physically_challenged ?? false,
      });
    }
  }, [user, profile, reset]);

  const save = useMutation({
    mutationFn: (input: UpdateProfileInput) => profileService.updateMe(input),
    onSuccess: async () => {
      toast.success('Profile updated');
      await refreshMe();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const onSubmit = (values: UpdateProfileInput) => {
    // Normalize empty strings to null so optional date/text fields validate + clear correctly
    const cleaned = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, v === '' ? null : v]),
    ) as UpdateProfileInput;
    save.mutate(cleaned);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pb-4">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => navigate('/account')}
          className="mt-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Manage Account</h1>
          <p className="text-sm text-muted-foreground">Update your personal information.</p>
        </div>
      </div>

      {/* Account Info (read-only) */}
      <Card>
        <SectionHeader icon={<Info className="h-3.5 w-3.5" />} title="Account Info" hint="Managed by admin" />
        <CardContent className="p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input value={user?.email ?? ''} disabled className="bg-muted/40" />
            </div>
            <div className="space-y-1.5">
              <Label>Employee Code</Label>
              <Input value={user?.employee_code ?? '—'} disabled className="bg-muted/40" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Basic Information */}
      <Card>
        <SectionHeader icon={<User className="h-3.5 w-3.5" />} title="Basic Information" />
        <CardContent className="p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="first_name">First Name</Label>
              <Input id="first_name" {...register('first_name')} />
              <FieldError message={errors.first_name?.message} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="last_name">Last Name</Label>
              <Input id="last_name" {...register('last_name')} />
              <FieldError message={errors.last_name?.message} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" {...register('phone')} />
              <FieldError message={errors.phone?.message} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dob">Date of Birth</Label>
              <Controller
                control={control}
                name="dob"
                render={({ field }) => (
                  <DatePicker id="dob" value={field.value ?? ''} onChange={(v) => field.onChange(v || null)} />
                )}
              />
              <FieldError message={errors.dob?.message} />
            </div>
            <div className="space-y-1.5">
              <Label>Gender</Label>
              <Controller
                control={control}
                name="gender"
                render={({ field }) => (
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Blood Group</Label>
              <Controller
                control={control}
                name="blood_group"
                render={({ field }) => (
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {BLOOD_GROUPS.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Marital Status</Label>
              <Controller
                control={control}
                name="marital_status"
                render={({ field }) => (
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {MARITAL.map((m) => (
                        <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="emergency_contact">Emergency Contact</Label>
              <Input id="emergency_contact" {...register('emergency_contact')} />
              <FieldError message={errors.emergency_contact?.message} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Family Information */}
      <Card>
        <SectionHeader icon={<Users className="h-3.5 w-3.5" />} title="Family Information" />
        <CardContent className="p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="father_name">Father's Name</Label>
              <Input id="father_name" {...register('father_name')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mother_name">Mother's Name</Label>
              <Input id="mother_name" {...register('mother_name')} />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="spouse_name">Spouse's Name</Label>
              <Input id="spouse_name" placeholder="Spouse's name (if applicable)" {...register('spouse_name')} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Address */}
      <Card>
        <SectionHeader icon={<MapPin className="h-3.5 w-3.5" />} title="Address" />
        <CardContent className="p-4 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="address_line1">Address Line 1</Label>
            <Input id="address_line1" {...register('address_line1')} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="address_line2">Address Line 2</Label>
            <Input id="address_line2" {...register('address_line2')} />
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="city">City</Label>
              <Input id="city" {...register('city')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="state">State</Label>
              <Input id="state" {...register('state')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="postal_code">PIN</Label>
              <Input id="postal_code" {...register('postal_code')} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Physically Challenged toggle */}
      <Card>
        <CardContent className="flex items-center justify-between p-4">
          <div>
            <p className="text-sm font-semibold text-foreground">Physically Challenged</p>
            <p className="text-xs text-muted-foreground">Person with disability</p>
          </div>
          <Controller
            control={control}
            name="physically_challenged"
            render={({ field }) => (
              <Switch
                checked={!!field.value}
                onChange={field.onChange}
                size="sm"
                aria-label="Physically Challenged"
              />
            )}
          />
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="flex justify-center gap-3 pt-1">
        <Button type="button" variant="outline" className="min-w-[140px]" onClick={() => navigate('/account')}>
          Cancel
        </Button>
        <Button type="submit" className="min-w-[180px]" disabled={save.isPending}>
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {save.isPending ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
}
